import { Component, OnInit } from '@angular/core';
import { HxPagedListDirective, HxStoreSelectComponent, HxWorkflowCategorySelectComponent } from 'hx-components';
import { HxCategoryService, HxRecipeService, HxUiLabelPipe, PagedList, toRouterQueryParams, UiLabel, WorkflowCategoryModel, WorkflowModel } from 'hx-services';
import { takeUntil } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzCollapseComponent, NzCollapsePanelComponent } from 'ng-zorro-antd/collapse';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NgFor } from '@angular/common';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import Timeout = NodeJS.Timeout;

@Component({
    selector: 'hc-workflow-list',
    templateUrl: './workflow-list.component.html',
    styleUrls: ['./workflow-list.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzButtonComponent, RouterLink, NzIconDirective, NzCollapseComponent, NzCollapsePanelComponent, NzRowDirective, NzColDirective, NzInputDirective, FormsModule, HxWorkflowCategorySelectComponent, HxStoreSelectComponent, NzWaveDirective, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzTbodyComponent, NgFor, NzButtonGroupComponent, NzPopconfirmDirective, HxUiLabelPipe, TranslocoPipe]
})
export class WorkflowListComponent extends HxPagedListDirective<WorkflowModel> implements OnInit {
  filter: {
    page?: number;
    limit?: number;
    orderBy?: string;
    workflowCategoryId?: number;
    query?: string;
    storeId?: number;
  } = {};
  workflowCategories: WorkflowCategoryModel[] = [];
  categoryTitleMap: Map<number, UiLabel> = new Map<number, UiLabel>();
  private timeoutId?: Timeout;

  constructor(
    override router: Router,
    override aRoute: ActivatedRoute,
    protected api: HxRecipeService,
    protected notification: NzNotificationService,
    protected tr: TranslocoService,
    protected categoryService: HxCategoryService,
  ) {
    super(router, aRoute);
    this.limit = 20;
  }

  ngOnInit() {
    this.loadCategories();
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => {
        const asNumberFn = (name: string, def?: number) => paramMap.get(name) ? Number(paramMap.get(name)) : def;
        this.filter.query = paramMap.get('query') ?? undefined;
        this.filter.workflowCategoryId = asNumberFn('workflowCategoryId');
        this.filter.storeId = asNumberFn('storeId');
        super.load(paramMap);
      });
  }

  applyFilters(delay?: number) {
    if (delay) {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.timeoutId = setTimeout(() => this.navigate(), delay);
    } else {
      this.navigate();
    }
  }

  async remove(id: number): Promise<void> {
    await this.api.removeWorkflow(id);
    this.loadData();
    this.notification.success(this.tr.translate('nav.Workflows'), this.tr.translate('nt.dataRemoved'));
  }

  reset() {
    this.filter = {page: 1, limit: 20};
    this.navigate();
  }

  private navigate() {
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: toRouterQueryParams(this.filter),
    });
  }

  private async loadCategories() {
    const result = await this.categoryService.getWorkflowCategories();
    this.workflowCategories = result.list;
    this.workflowCategories.forEach(wc => this.categoryTitleMap.set(wc.id, wc.title));
  }

  protected loadList(): Promise<PagedList<WorkflowModel>> {
    return this.api.getWorkflowList({
      page: this.filter.page,
      limit: this.filter.limit,
      orderBy: this.filter.orderBy,
      workflowCategoryId: this.filter.workflowCategoryId,
      query: this.filter.query,
      storeId: this.filter.storeId
    });
  }
}
